// react
import { useCallback, useMemo, useState } from 'react';

// components
import {
  Dialog,
  Editor,
  ProfilePreview,
  Switch,
  TablePage,
  createFieldHelper,
} from '@karehero/llama';

// store
import {
  useAssociateKindeAccountWithInviteMutation,
  useGetAllInvitedMemberQuery,
  useUpdateInviteAwaitingFeeMutation,
} from 'store/api/invitedMember';
import { useReinviteMemberMutation } from 'store/api/organization';
import { FieldType, InvitedMember } from '@karehero/models';
import { newToast } from 'store/slices/toastSlice';
import { useDispatch } from 'react-redux';

// tanstack
import { createColumnHelper } from '@tanstack/react-table';
import { Link } from 'react-router-dom';

// consts
const KINDE_USER_LENGTH = 35;

// columns
const columnHelper = createColumnHelper<InvitedMember>();

const columns = [
  columnHelper.accessor('email', {
    header: 'Email',
  }),
  columnHelper.accessor('firstName', {
    header: 'First Name',
  }),
  columnHelper.accessor('organizationId', {
    header: 'Organization ID',
    cell: (info) => {
      const row = info.cell.row.original;
      return (
        <Link to={`/admin/organization/${row.organizationId}/member`}>
          {row.organizationId}
        </Link>
      );
    },
  }),
];

const kindeDetailsFields = createFieldHelper<{
  kindeID: string;
  inviteID: string;
  inviteEmail: string;
  organization: string;
}>({
  inviteID: {
    label: 'Invite ID',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
  },
  inviteEmail: {
    label: 'Invite Email',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
  },
  kindeID: {
    label: 'Kinde ID',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
    validationRules: [
      {
        type: 'min-length',
        value: 2,
      },
    ],
  },
  organization: {
    label: 'Organization',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
  },
});

/**
 * ManageInvitedMembers shows the invited members.
 */
const ManageInvitedMembers = () => {
  // state
  const [isProfilePreviewOpen, setIsProfilePreviewOpen] = useState(false);
  const [isShowAssociateKindeAccount, setIsShowAssociateKindeAccount] =
    useState(false);
  const [selectedProfileID, setSelectedProfileID] = useState<string | null>(
    null,
  );
  const [filterKareHeroEmail, setFilterKareHeroEmail] = useState(false);
  const [kindeDetails, setKindeDetails] = useState<
    Partial<{ kindeID: string; inviteEmail: string; inviteID: string }>
  >({});

  // hooks
  const { data: invitedMembers } = useGetAllInvitedMemberQuery();
  const [reinviteMember] = useReinviteMemberMutation();
  const [updateInviteAwaitingFee] = useUpdateInviteAwaitingFeeMutation();
  const [associateKindeAccount] = useAssociateKindeAccountWithInviteMutation();
  const dispatch = useDispatch();

  // memos
  const selectedProfile = useMemo(() => {
    if (!selectedProfileID || !invitedMembers) return null;
    return invitedMembers.find(
      (invitedMember) => invitedMember.id === selectedProfileID,
    );
  }, [selectedProfileID, invitedMembers]);

  const rows = useMemo(() => {
    if (!invitedMembers) return [];
    return invitedMembers
      .filter((invitedMember) => {
        if (!filterKareHeroEmail) return true;
        return !invitedMember.email.toLowerCase().includes('@karehero.com');
      })
      .map((invitedMember) => ({
        ...invitedMember,
      }));
  }, [invitedMembers, filterKareHeroEmail]);

  const selectedProfileInitials = useMemo(() => {
    if (!selectedProfile) return '';
    return (selectedProfile?.firstName?.[0] || '').toUpperCase();
  }, [selectedProfile]);

  // methods
  const handleReinviteMember = useCallback(
    async (id?: string) => {
      if (!id) return;
      const res = await reinviteMember(id);

      if ('error' in res) {
        console.error(res.error);
        return;
      }
      dispatch(
        newToast({
          title: 'Invitation Success',
          description: `${!selectedProfile?.inviteSentAt ? 'Sent' : 'Resent'} invitation to ${selectedProfile?.email}`,
        }),
      );
      setIsProfilePreviewOpen(false);
    },
    [dispatch, selectedProfile, reinviteMember],
  );

  const handleUpdateAwaitingFee = useCallback(
    async (selectedProfile: InvitedMember) => {
      if (!selectedProfile) return;

      const res = await updateInviteAwaitingFee(selectedProfile);
      if ('error' in res) {
        console.error(res.error);
        return;
      }
      dispatch(
        newToast({
          title: 'Fee Updated',
          description: `Sent invitation to ${selectedProfile.email} using invite button`,
          type: 'success',
        }),
      );

      setIsProfilePreviewOpen(false);
    },
    [dispatch, updateInviteAwaitingFee],
  );

  const handleAssociateKindeAccount = useCallback(
    async (selectedProfile: InvitedMember, kindeID: string) => {
      const res = await associateKindeAccount({
        inviteID: selectedProfile.id,
        kindeID,
      });
      if ('error' in res) {
        console.error(res.error);
        return;
      }
      dispatch(
        newToast({
          title: 'Account Linked Successfully',
          description: `Associated invite with kinde account and created new account successfully`,
          type: 'success',
        }),
      );

      setKindeDetails({});
      setIsProfilePreviewOpen(false);
      setIsShowAssociateKindeAccount(false);
    },
    [dispatch, associateKindeAccount],
  );

  return (
    <>
      <TablePage
        title='Invited Members'
        columns={columns}
        rows={rows}
        rowsPerPage={10}
        actions={[
          {
            label: 'View',
            onPress: (invitedMember: InvitedMember) => {
              setSelectedProfileID(invitedMember.id);
              setIsProfilePreviewOpen(true);
            },
          },
        ]}
        toolbarActions={
          <Switch
            id='filterKareHeroEmail'
            value={filterKareHeroEmail}
            onChange={setFilterKareHeroEmail}
            label='Filter KareHero Emails'
            size='sm'
            isNoWrap
          />
        }
      />
      <Dialog
        title='Associate Kinde Account with Invite'
        variant='sheet'
        isOpen={isShowAssociateKindeAccount}
        setIsOpen={setIsShowAssociateKindeAccount}
        zIndex={1}
      >
        <Editor
          onSave={() => {
            if (!selectedProfile) return;
            if (!kindeDetails.kindeID || !kindeDetails.inviteID) return;
            if (selectedProfile.id !== kindeDetails.inviteID) return;

            handleAssociateKindeAccount(selectedProfile, kindeDetails.kindeID);
          }}
          onCancel={() => setIsShowAssociateKindeAccount(false)}
          saveLabel='Save'
          cancelLabel='Cancel'
          isWithArrows={false}
          isActionsDisabled={false}
          value={{ ...kindeDetails, inviteID: selectedProfile?.id }}
          onChange={(val) =>
            setKindeDetails({ ...kindeDetails, kindeID: val.kindeID })
          }
          fields={kindeDetailsFields}
          isDisabled={!selectedProfile}
          isFloatingActions={true}
          isForm={true}
          isSaveDisabled={
            !kindeDetails.kindeID ||
            kindeDetails.kindeID.length !== KINDE_USER_LENGTH
          }
        />
      </Dialog>
      <Dialog
        variant='sheet'
        isOpen={isProfilePreviewOpen}
        setIsOpen={setIsProfilePreviewOpen}
        zIndex={1}
      >
        <ProfilePreview
          fallback={selectedProfileInitials}
          name={`${selectedProfile?.firstName || ''}`}
          role={selectedProfile?.role?.name}
          email={selectedProfile?.email}
          onClose={() => setIsProfilePreviewOpen(false)}
          actions={[
            {
              label: !selectedProfile?.inviteSentAt ? 'Invite' : 'Reinvite',
              onPress: () => handleReinviteMember(selectedProfile?.id),
              isDisabled: selectedProfile?.isAwaitingFee,
            },
            {
              label: 'Mark Fee Paid',
              onPress: () =>
                selectedProfile && handleUpdateAwaitingFee(selectedProfile),
              isDisabled: !selectedProfile?.isAwaitingFee,
            },
            {
              label: 'Associate Existing Kinde Account',
              onPress: () => {
                setKindeDetails({
                  inviteID: selectedProfile?.id,
                  inviteEmail: selectedProfile?.email,
                });
                setIsShowAssociateKindeAccount(true);
              },
              isDisabled: isShowAssociateKindeAccount,
            },
          ]}
          isActionsDisabled
        />
      </Dialog>
    </>
  );
};

export default ManageInvitedMembers;
